import React, { useEffect } from 'react';
import useEkyc from './hooks/useEkyc';
import { Button, Card, StepIndicator, Spinner, ErrorAlert } from '../../components/shared';
import OcrStep from './components/OcrStep';
import FaceMatchStep from './components/FaceMatchStep';
import LivenessStep from './components/LivenessStep';
import ConfirmStep from './components/ConfirmStep';
import ResultStep from './components/ResultStep';

const STEP_LABELS = {
  OCR: 'CCCD/CMND',
  FACE_MATCH: 'Khuôn mặt',
  LIVENESS: 'Khuôn mặt',
  CONFIRM: 'Xác nhận',
};

function StartScreen({ onStart, loading, error }) {
  return (
    <div className="mx-auto max-w-lg">
      <Card>
        <div className="flex flex-col items-center gap-6 py-8">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-blue-50">
            <svg className="h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          </div>
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900">Xác thực danh tính (eKYC)</h2>
            <p className="mt-2 text-sm text-gray-500">
              Xác thực CCCD/CMND và khuôn mặt để mở tài khoản chứng khoán
            </p>
          </div>
          <div className="w-full space-y-3 rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-700">1</span>
              <span>Chụp ảnh mặt trước và mặt sau CCCD/CMND</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-700">2</span>
              <span>Chụp ảnh hoặc quay video xác thực khuôn mặt</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-700">3</span>
              <span>Xác nhận thông tin và hoàn tất</span>
            </div>
          </div>

          {error && <ErrorAlert error={error} className="w-full" />}
          <Button onClick={onStart} loading={loading} size="lg" className="w-full">
            Bắt đầu xác thực
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default function EkycPage() {
  const {
    currentStep,
    visibleSteps,
    extractedData,
    validations,
    rawResponse,
    loading,
    error,
    sessionStatus,
    isCompleted,
    initState,
    checkActiveSession,
    initSession,
    submitOcr,
    submitFaceMatch,
    submitLiveness,
    confirm,
    clearError,
    restartFlow,
  } = useEkyc();

  useEffect(() => {
    checkActiveSession();
  }, []);

  if (initState === 'IDLE' || initState === 'CHECKING') {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (initState === 'NO_SESSION' && currentStep < 0) {
    return (
      <StartScreen
        onStart={() => initSession()}
        loading={loading}
        error={error}
      />
    );
  }

  const stepItems = visibleSteps.map((key) => ({
    key,
    label: STEP_LABELS[key] || key,
  }));

  const currentStepKey = isCompleted ? null : visibleSteps[currentStep];

  const renderStep = () => {
    if (isCompleted) {
      return <ResultStep sessionStatus={sessionStatus} extractedData={extractedData} />;
    }

    switch (currentStepKey) {
      case 'OCR':
        return (
          <OcrStep
            onSubmit={(front, back) => { clearError(); submitOcr(front, back); }}
            loading={loading}
            error={error}
          />
        );
      case 'FACE_MATCH':
        return (
          <FaceMatchStep
            onSubmit={(selfie) => { clearError(); submitFaceMatch(selfie); }}
            loading={loading}
            error={error}
          />
        );
      case 'LIVENESS':
        return (
          <LivenessStep
            onSubmit={(video) => { clearError(); submitLiveness(video); }}
            loading={loading}
            error={error}
          />
        );
      case 'CONFIRM':
        return (
          <ConfirmStep
            extractedData={extractedData}
            validations={validations}
            rawResponse={rawResponse}
            onConfirm={() => { clearError(); confirm(); }}
            loading={loading}
            error={error}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Xác thực danh tính (eKYC)</h1>
        <p className="mt-1 text-sm text-gray-500">Hoàn thành các bước dưới đây để xác thực danh tính của bạn</p>
      </div>

      {!isCompleted && (
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <StepIndicator steps={stepItems} currentStep={currentStep} />
          </div>
          {currentStep > 0 && (
            <button
              type="button"
              onClick={restartFlow}
              className="ml-4 shrink-0 text-sm text-gray-500 underline hover:text-gray-700"
            >
              Làm lại từ đầu
            </button>
          )}
        </div>
      )}

      {renderStep()}
    </div>
  );
}
